"use client";

import React, {  useState, useEffect } from "react";
import { CustomerStatusCode, CustomerType } from "../services/customer-service";
import EmptyState from "../../../components/custom/empty-state";

export interface CustomerRow {
  id: number;
  code?: string | null;
  name: string;
  phone: string;
  address: string;
  total_spent: number;
  completed_orders: number;
  statusCode: CustomerStatusCode;
  created_at: string;
  updated_at: string;
  email: string;
  type_customer: CustomerType;
  discount_percent: number;
  default_payment_method?: number;
  requires_counting?: number;
  sale_id?: number | null;
  saleName?: string | null;
  sale?: { id: number; name: string } | null;
}

interface Props {
  customers: CustomerRow[];
  total?: number;
  loading?: boolean;
  onEdit?: (row: CustomerRow) => void;
  onViewDetail?: (row: CustomerRow) => void;
  /** Cấp tài khoản đăng nhập app cho khách — khách không tự đăng ký được. */
  onIssueAccount?: (row: CustomerRow) => void;
  /** Đặt lại mật khẩu cho khách đã có tài khoản. */
  onResetAccountPassword?: (row: CustomerRow) => void;
  /** Mở modal cấu hình SLA riêng cho khách (mốc nào không cấu hình thì dùng mốc chung). */
  onOpenSlaConfig?: (row: CustomerRow) => void;
  /**
   * `customers.id` của những khách ĐÃ có tài khoản app.
   *
   * Khoá là CHUỖI, không phải số: `customers.id` là `bigint` nên API trả ra chuỗi, dù
   * `CustomerRow.id` khai kiểu `number`. So sánh bằng số sẽ luôn trượt và mọi khách đều
   * bị coi là chưa có tài khoản.
   *
   * Dùng `Set` thay vì mảng vì mỗi dòng đều phải tra một lần.
   * `undefined` = chưa tra được; lúc đó bảng vẫn hiện nút "Cấp tài khoản".
   */
  accountCustomerIds?: Set<string>;
  /**
   * `customers.id` của những khách ĐANG có cấu hình SLA riêng (ít nhất 1 mốc).
   *
   * Khoá là CHUỖI, cùng lý do với `accountCustomerIds`: `customers.id` là `bigint`, một số API
   * trả ra chuỗi dù `CustomerRow.id` khai kiểu `number` — so bằng số sẽ trượt và mọi khách đều
   * hiện "Dùng chung" dù đã cấu hình riêng.
   *
   * `undefined` = chưa tra được (order-service lỗi/chưa deploy) → cột hiện "—", không suy diễn có/không.
   */
  slaOverrideCustomerIds?: Set<string>;
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  page?: number;   // optional controlled page
  limit?: number;  // optional controlled limit
}

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const CUSTOMER_TYPE_LABEL: Record<CustomerType, string> = {
  [CustomerType.ENTERPRISE]: "Doanh nghiệp",
  [CustomerType.HOUSEHOLD]: "Hộ gia đình",
};

const CustomerTable: React.FC<Props> = ({
  customers,
  total = 0,
  loading = false,
  onEdit,
  onViewDetail,
  onIssueAccount,
  onResetAccountPassword,
  onOpenSlaConfig,
  accountCustomerIds,
  slaOverrideCustomerIds,
  onPageChange,
  onLimitChange,
  page: pageProp,
  limit: limitProp,
}) => {
  // ==== LOCAL PAGINATION STATE ====
  const [pageState, setPageState] = useState(1);
  const [limitState, setLimitState] = useState(PAGE_SIZE_OPTIONS[0]);

  // Keep internal state in sync when controlled props change
  useEffect(() => { if (typeof pageProp === 'number') setPageState(pageProp); }, [pageProp]);
  useEffect(() => { if (typeof limitProp === 'number') setLimitState(limitProp); }, [limitProp]);

  const page = typeof pageProp === 'number' ? pageProp : pageState;
  const limit = typeof limitProp === 'number' ? limitProp : limitState;
  const totalPages = Math.max(1, Math.ceil(total / (limit || 1)));

  useEffect(() => {
    onPageChange?.(page);
  }, [page]);

  useEffect(() => {
    onLimitChange?.(limit);
    if (typeof pageProp !== 'number') setPageState(1); // reset local page when limit changes in uncontrolled mode
  }, [limit]);

  // ==== MAIN TABLE ====
  // Chỉ PHẦN BẢNG (thead+tbody) cuộn riêng, thead dính lại (sticky) — header trang, tab, ô tìm kiếm
  // và footer phân trang bên dưới luôn cố định, không bị đẩy ra ngoài viewport khi danh sách dài.
  // `max-h-[65vh]` theo đúng ngưỡng đang dùng ở bảng đơn hàng (order-index-page.tsx) để nhất quán.
  // "Đang tải" / rỗng render trong cùng khung để bố cục không nhảy.
  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto overflow-y-auto max-h-[65vh]">
      {loading ? (
        <div className="flex justify-center py-16 text-sm text-slate-500">Đang tải dữ liệu...</div>
      ) : !customers.length ? (
        <EmptyState message="Chưa có khách hàng" description="Không tìm thấy khách hàng khớp bộ lọc." />
      ) : (
      <table className="min-w-full border-collapse text-sm border border-slate-200">
        <thead className="bg-slate-50 text-slate-700 whitespace-nowrap sticky top-0 z-10">
          <tr>
            <th className="px-4 py-3 text-center font-semibold border border-slate-200">STT</th>
            <th className="px-4 py-3 text-center font-semibold border border-slate-200">Mã KH</th>
            <th className="px-4 py-3 text-center font-semibold border border-slate-200">Tên khách hàng</th>
            <th className="px-4 py-3 text-center font-semibold border border-slate-200">NV Sale</th>
            <th className="px-4 py-3 text-center font-semibold border border-slate-200">Số điện thoại</th>
            <th className="px-4 py-3 text-center font-semibold border border-slate-200">Địa chỉ</th>
            <th className="px-4 py-3 text-center font-semibold border border-slate-200">Chiết Khấu(%)</th>
            <th className="px-4 py-3 text-center font-semibold border border-slate-200">Email</th>
            <th className="px-4 py-3 text-center font-semibold border border-slate-200">Loại khách hàng</th>
            <th className="px-4 py-3 text-center font-semibold border border-slate-200">Kiểm đếm</th>
            <th className="px-4 py-3 text-center font-semibold border border-slate-200">SLA riêng</th>
            <th className="px-4 py-3 text-center font-semibold border border-slate-200">Hành động</th>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-100 text-slate-700">
          {customers.map((c, index) => (
            <tr key={c.id} className="hover:bg-slate-50 transition group">
              <td className="px-4 py-3 text-center text-slate-500">
                {(page - 1) * limit + index + 1}
              </td>
              <td className="px-4 py-3 font-semibold text-slate-700 border border-slate-200 whitespace-nowrap text-center">
                {c.code ? (
                  <span className="inline-block rounded bg-slate-100 px-2 py-0.5 text-xs font-mono font-medium text-slate-800">
                    {c.code}
                  </span>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </td>
              <td className="px-4 py-3 font-medium border border-slate-200">{c.name}</td>
              <td className="px-4 py-3 border border-slate-200 text-center whitespace-nowrap">
                {c.saleName || c.sale?.name ? (
                  <span className="inline-block rounded bg-blue-50 text-blue-700 px-2 py-0.5 text-xs font-medium">
                    {c.saleName || c.sale?.name}
                  </span>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </td>
              <td className="px-4 py-3 border border-slate-200">{c.phone}</td>
              <td className="px-4 py-3 border border-slate-200">{c.address}</td>
              <td className="px-4 py-3 border border-slate-200 text-right">{c.discount_percent}</td>
              <td className="px-4 py-3 border border-slate-200">{c.email}</td>
              <td className="px-4 py-3 border border-slate-200">
                {CUSTOMER_TYPE_LABEL[c.type_customer] ?? "-"}
              </td>
              <td className="px-4 py-3 text-center border border-slate-200">
                {Number(c.requires_counting) === 1 ? (
                  <span
                    title="Đơn của khách này sẽ bật kiểm đếm khi nhận & khi giao"
                    className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700 whitespace-nowrap"
                  >
                    Có kiểm đếm
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500 whitespace-nowrap">
                    Không
                  </span>
                )}
              </td>

              {/* === SLA RIÊNG === */}
              <td className="px-4 py-3 text-center border border-slate-200">
                {slaOverrideCustomerIds === undefined ? (
                  <span className="text-slate-300">—</span>
                ) : slaOverrideCustomerIds.has(String(c.id)) ? (
                  <span
                    title="Khách này có ít nhất 1 mốc SLA riêng, khác giờ chung"
                    className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-medium text-indigo-700 whitespace-nowrap"
                  >
                    Cấu hình riêng
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500 whitespace-nowrap">
                    Dùng chung
                  </span>
                )}
              </td>

              {/* === ACTION BUTTONS === */}
              <td className="px-4 py-3 text-center">
                <div className="flex justify-center gap-2">
                  {/*
                    === TÀI KHOẢN APP ===
                    Đã có tài khoản → nút Đặt lại mật khẩu (hổ phách).
                    Chưa có, HOẶC chưa tra được trạng thái → nút Cấp tài khoản (teal).

                    Khi không tra được (user service lỗi, endpoint chưa deploy) vẫn phải hiện
                    nút Cấp, KHÔNG được ẩn hết: ẩn đi thì nhân viên mất chức năng mà không
                    hiểu vì sao. Bấm nhầm cũng an toàn — backend chặn cấp trùng và báo rõ
                    "Khách hàng này đã có tài khoản".
                  */}
                  {accountCustomerIds?.has(String(c.id))
                      ? onResetAccountPassword && (
                          <button
                            type="button"
                            onClick={() => onResetAccountPassword(c)}
                            aria-label="Đặt lại mật khẩu app"
                            className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-amber-500 text-white shadow-sm transition hover:bg-amber-600"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              viewBox="0 0 24 24"
                              className="h-4 w-4"
                            >
                              <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
                              <path d="M21 3v5h-5" />
                            </svg>
                            <span className="pointer-events-none absolute bottom-11 left-1/2 hidden -translate-x-1/2 whitespace-nowrap
                              rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white group-hover/action:block">
                              Đặt lại mật khẩu app
                            </span>
                          </button>
                        )
                      : onIssueAccount && (
                          <button
                            type="button"
                            onClick={() => onIssueAccount(c)}
                            aria-label="Cấp tài khoản app"
                            className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-teal-500 text-white shadow-sm transition hover:bg-teal-600"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              viewBox="0 0 24 24"
                              className="h-4 w-4"
                            >
                              <rect x="4" y="10" width="16" height="11" rx="2" />
                              <path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v3" />
                            </svg>
                            <span className="pointer-events-none absolute bottom-11 left-1/2 hidden -translate-x-1/2 whitespace-nowrap
                              rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white group-hover/action:block">
                              Cấp tài khoản app
                            </span>
                          </button>
                        )}

                  {/* === EDIT === */}
                  {onEdit && (
                    <button
                      type="button"
                      onClick={() => onEdit(c)}
                      aria-label="Sửa"
                      className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-blue-500 text-white shadow-sm transition hover:bg-blue-600"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                      >
                        <path d="M21.731 2.269a2.625 2.625 0 0 0-3.714 0l-1.157 1.157 
                          3.714 3.714 1.157-1.157a2.625 2.625 0 0 0 0-3.714Z" />
                        <path d="M19.513 8.199l-3.714-3.714-9.193 
                          9.193a5.25 5.25 0 0 0-1.32 2.214l-.8 
                          2.684a.75.75 0 0 0 .933.933l2.684-.8a5.25 
                          5.25 0 0 0 2.214-1.32l9.193-9.193Z" />
                        <path d="M5.25 5.25h3a.75.75 0 0 1 
                          0 1.5h-3A1.75 1.75 0 0 0 3.5 
                          8.5v10A1.75 1.75 0 0 0 5.25 
                          20.25h10a1.75 1.75 0 0 0 
                          1.75-1.75v-3a.75.75 0 0 1 
                          1.5 0v3A3.25 3.25 0 0 1 
                          15.25 21.75h-10A3.25 3.25 
                          0 0 1 2 18.5v-10A3.25 3.25 
                          0 0 1 5.25 5.25Z" />
                      </svg>
                      <span className="pointer-events-none absolute bottom-11 left-1/2 hidden -translate-x-1/2 whitespace-nowrap 
                        rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white group-hover/action:block">
                        Sửa
                      </span>
                    </button>
                  )}

                  {/* === CẤU HÌNH SLA RIÊNG === */}
                  {onOpenSlaConfig && (
                    <button
                      type="button"
                      onClick={() => onOpenSlaConfig(c)}
                      aria-label="Cấu hình SLA riêng"
                      className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-indigo-500 text-white shadow-sm transition hover:bg-indigo-600"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                      >
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 7v5l3 3" />
                      </svg>
                      <span className="pointer-events-none absolute bottom-11 left-1/2 hidden -translate-x-1/2 whitespace-nowrap
                        rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white group-hover/action:block">
                        Cấu hình SLA riêng
                      </span>
                    </button>
                  )}

                  {/* === VIEW === */}
                  {onViewDetail && (
                    <button
                      type="button"
                      onClick={() => onViewDetail(c)}
                      aria-label="Xem chi tiết"
                      className="group/action relative grid h-9 w-9 place-items-center rounded-full bg-slate-500 text-white shadow-sm transition hover:bg-slate-600"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="h-4 w-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 010-.639
                            C3.423 7.51 7.36 4.5 12 4.5c4.638 0 
                            8.573 3.007 9.963 7.178.07.207.07.431 
                            0 .639C20.577 16.49 16.64 19.5 12 
                            19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span className="pointer-events-none absolute bottom-11 left-1/2 hidden -translate-x-1/2 whitespace-nowrap 
                        rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white group-hover/action:block">
                        Xem
                      </span>
                    </button>
                  )}

                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      )}
      </div>

      {/* === PAGINATION === */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between p-4 text-sm text-slate-600 border-t border-slate-100">
      <p>
        {total > 0 && customers.length > 0
          ? (() => {
              const start = (page - 1) * limit + 1;
              const end = Math.min(start + customers.length - 1, total);
              return `Hiển thị ${start}–${end} trong tổng ${total} khách hàng.`;
            })()
          : "Không có khách hàng nào."}
      </p>
        <div className="flex items-center gap-3">
          <label>Hiển thị</label>
          <select
            value={limit}
            onChange={(e) => {
              const next = Number(e.target.value);
              onLimitChange?.(next);
              if (typeof limitProp !== 'number') setLimitState(next);
            }}
            className="rounded-md border border-slate-300 px-2 py-1 text-sm"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span>mỗi trang</span>

          <button
            type="button"
            disabled={page === 1}
            onClick={() => {
              const next = Math.max(1, page - 1);
              onPageChange?.(next);
              if (typeof pageProp !== 'number') setPageState(next);
            }}
            className="rounded-md border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-40"
          >
            Trước
          </button>
          <span>
            Trang {page}/{totalPages}
          </span>
          <button
            type="button"
            disabled={page === totalPages}
            onClick={() => {
              const next = Math.min(totalPages, page + 1);
              onPageChange?.(next);
              if (typeof pageProp !== 'number') setPageState(next);
            }}
            className="rounded-md border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-40"
          >
            Sau
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerTable;
